import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, MapPin, CalendarPlus, Apple, Share2, X } from "lucide-react";
import Header from "@/components/Header";
import { api, gbp2 } from "@/lib/api";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const reference = params.get("reference");
  const [booking, setBooking] = useState(null);
  const [status, setStatus] = useState("checking");
  const [clinicAddress, setClinicAddress] = useState("");
  const [referralAmount, setReferralAmount] = useState(20);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setClinicAddress(r.data?.clinic_address || "");
      if (r.data?.referral_amount_gbp != null) setReferralAmount(Number(r.data.referral_amount_gbp));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    const poll = async (attempt = 0) => {
      if (cancelled) return;
      try {
        if (sessionId) {
          const r = await api.get(`/payments/status/${sessionId}`);
          if (r.data.payment_status === "paid" && r.data.booking) {
            setBooking(r.data.booking); setStatus("ok"); return;
          }
          if (r.data.payment_status === "expired" || r.data.payment_status === "failed") {
            setStatus("failed"); return;
          }
        } else if (reference) {
          const r = await api.get(`/bookings/reference/${reference}`);
          setBooking(r.data); setStatus("ok"); return;
        } else {
          setStatus("failed"); return;
        }
        if (attempt < 15) setTimeout(() => poll(attempt + 1), 2000);
        else setStatus("timeout");
      } catch { setStatus("failed"); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, reference]);

  const copyRef = () => {
    if (!booking) return;
    navigator.clipboard.writeText(booking.reference);
    toast.success("Reference copied");
  };

  if (status === "checking" || status === "timeout") {
    return (
      <>
        <Header minimal />
        <main className="min-h-[60vh] max-w-3xl mx-auto px-6 lg:px-10 pt-24 pb-24 text-center">
          <div className="inline-block h-8 w-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin" />
          <p className="mt-6 mono-label text-stone-600">
            {status === "checking" ? "Confirming your payment…" : "Still confirming — please give us a moment."}
          </p>
        </main>
      </>
    );
  }

  if (status === "failed") {
    return (
      <>
        <Header minimal />
        <main className="min-h-[60vh] max-w-3xl mx-auto px-6 lg:px-10 pt-24 pb-24 text-center">
          <h1 className="font-serif text-4xl font-light text-stone-900">Payment not confirmed</h1>
          <p className="mt-4 text-stone-600">We couldn't confirm your payment. If you were charged, please contact us and we'll sort it out.</p>
          <Link to="/" data-testid="back-home" className="mt-8 inline-block border border-stone-900 px-6 py-3 mono-label hover:bg-stone-900 hover:text-white transition">Back to CLAÈ Aesthetics</Link>
        </main>
      </>
    );
  }

  const b = booking;
  const firstName = (b.client_name || "").split(" ")[0] || "";
  const treatmentsLine = (b.items || []).map((i) => `${i.name}${i.quantity > 1 ? ` × ${i.quantity}` : ""}`).join(", ");

  return (
    <>
      <Header minimal />
      <main className="min-h-screen bg-[#FAF9F6]" data-testid="booking-confirmation">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 pt-14 pb-24 fade-in">

          {/* Gold rule */}
          <div className="flex justify-center">
            <span className="inline-block h-[2px] w-10 bg-[#C5A059]" />
          </div>

          <h1 className="mt-6 text-center font-serif text-5xl sm:text-6xl font-light tracking-tight text-stone-900">
            Thank You
          </h1>
          <p className="mt-4 text-center text-lg text-stone-800 font-serif italic">Your appointment is confirmed.</p>
          <p className="mt-3 text-center text-stone-600 text-sm max-w-md mx-auto leading-relaxed">
            {firstName ? `${firstName}, thank ` : "Thank "}you for booking with CLAÈ Aesthetics. We look forward to seeing you. A confirmation email is on its way with your details.
          </p>

          {/* Booking reference — hero card */}
          <div className="mt-10 border hairline bg-white p-6 sm:p-8 text-center" data-testid="booking-reference-card">
            <p className="mono-label text-stone-500 text-[11px]">BOOKING REFERENCE</p>
            <p className="mt-2 font-mono text-2xl sm:text-3xl tracking-widest text-stone-900" data-testid="booking-reference">
              {b.reference}
            </p>
            <button
              data-testid="copy-reference"
              onClick={copyRef}
              className="mt-4 inline-flex items-center gap-2 mono-label border hairline px-3 py-2 text-[11px] hover:bg-stone-900 hover:text-white transition"
            >
              <Copy size={12} /> Copy reference
            </button>
          </div>

          {/* Appointment details */}
          <div className="mt-6 border hairline bg-white p-6 sm:p-8 space-y-4">
            <p className="mono-label text-stone-500 text-[11px]">APPOINTMENT</p>
            <Row label="Treatment" value={treatmentsLine} />
            <Row label="Date" value={formatDate(b.date)} />
            <Row label="Time" value={b.time} />
            <Row label="Duration" value={`${b.duration_min} min`} />
            {clinicAddress && (
              <Row
                label="Location"
                value={(
                  <span className="inline-flex items-start gap-1 justify-end text-right">
                    <MapPin size={14} className="mt-[3px] shrink-0 text-stone-500" />
                    <span>{clinicAddress}</span>
                  </span>
                )}
              />
            )}
          </div>

          {/* Payment */}
          <div className="mt-6 border hairline bg-white p-6 sm:p-8 space-y-3" data-testid="payment-block">
            <p className="mono-label text-stone-500 text-[11px]">PAYMENT</p>
            <Row label="Subtotal" value={gbp2(b.subtotal)} />
            {Number(b.referral_credit || 0) > 0 && (
              <Row
                label="Referral"
                value={<span data-testid="payment-referral-value">− {gbp2(b.referral_credit)}</span>}
              />
            )}
            {Number(b.stored_credit_used || 0) > 0 && (
              <Row label="Credit applied" value={<>− {gbp2(b.stored_credit_used)}</>} />
            )}
            {Number(b.loyalty_discount || 0) > 0 && (
              <Row label="Loyalty reward" value={<>− {gbp2(b.loyalty_discount)}</>} />
            )}
            {(Number(b.referral_credit || 0) > 0
              || Number(b.stored_credit_used || 0) > 0
              || Number(b.loyalty_discount || 0) > 0) && (
              <Row
                label="Total"
                value={<span data-testid="payment-total-value">{gbp2(b.final_total != null ? b.final_total : b.subtotal)}</span>}
              />
            )}
            <Row label="Deposit paid" value={gbp2(b.amount_paid)} strong />
            {b.remaining_balance > 0 && (
              <Row label="Balance in clinic" value={<span data-testid="payment-balance-value">{gbp2(b.remaining_balance)}</span>} />
            )}
          </div>

          {/* Add to Calendar */}
          <div className="mt-6 border hairline bg-white p-6 sm:p-8" data-testid="add-to-calendar-block">
            <p className="mono-label text-stone-500 text-[11px]">ADD TO CALENDAR</p>
            <p className="mt-2 text-stone-600 text-xs">One tap and your appointment slots straight into your calendar.</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                data-testid="add-to-google-calendar"
                href={googleCalendarUrl(b, clinicAddress, treatmentsLine)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-stone-900 text-stone-900 py-3 mono-label text-xs hover:bg-stone-900 hover:text-white transition"
              >
                <CalendarPlus size={14} /> Google Calendar
              </a>
              <button
                data-testid="add-to-apple-calendar"
                onClick={() => downloadIcs(b, clinicAddress, treatmentsLine)}
                className="inline-flex items-center justify-center gap-2 border border-stone-900 text-stone-900 py-3 mono-label text-xs hover:bg-stone-900 hover:text-white transition"
              >
                <Apple size={14} /> Apple Calendar
              </button>
            </div>
            <p className="mt-3 text-[10px] text-stone-400">Apple Calendar also works on iPhone, iPad, Mac and Outlook. Time zone: Europe/London.</p>
          </div>

          {/* Referral teaser — code intentionally NOT shown here. The
              client receives their personal code via the post-treatment
              email after their first completed appointment; on this
              screen we just plant the idea. Amount is pulled live from
              admin settings so a policy change (e.g. £20 → £25) flows
              through immediately. */}
          <div className="mt-6 border hairline p-6 sm:p-8" style={{ backgroundColor: "#F4EDDE" }} data-testid="share-teaser-block">
            <p className="mono-label text-[11px]" style={{ color: "#8A6E30" }}>SHARE THE GLOW</p>
            <p className="mt-3 font-serif text-xl sm:text-2xl text-stone-900 leading-snug">
              Love CLAÈ? Share it with a friend.
            </p>
            <p className="mt-3 text-stone-700 text-sm leading-relaxed">
              Your friend can receive <strong>£{referralAmount} off</strong> their first CLAÈ appointment, and you'll receive <strong>£{referralAmount} CLAÈ credit</strong> when they complete their appointment.
            </p>
            <p className="mt-3 text-[11px] text-stone-500 leading-relaxed" data-testid="share-teaser-note">
              Your personal referral code will be available after your appointment.
            </p>
          </div>

          {/* Manual, optional return button — no auto redirect. */}
          <div className="mt-12 flex justify-center">
            <Link
              to="/"
              data-testid="back-to-clae"
              className="border border-stone-900 px-8 py-3 mono-label text-stone-900 hover:bg-stone-900 hover:text-white transition"
            >
              Back to CLAÈ Aesthetics
            </Link>
          </div>

          <p className="mt-10 text-center text-[11px] text-stone-400">
            Please quote your reference if you need to contact us. This page will stay open — no need to navigate away.
          </p>
        </div>
      </main>
    </>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-stone-600">{label}</span>
      <span className={`text-right ${strong ? "text-stone-900 font-medium" : "text-stone-900"}`}>{value}</span>
    </div>
  );
}

/* ── Share icon components — kept small so the modal grid tiles are uniform ─ */
const IconWhatsApp = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="#25D366" aria-hidden><path d="M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.5 0 .2 5.29.2 11.83c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.83 11.83 0 0 0 5.66 1.44h.01c6.55 0 11.86-5.29 11.86-11.83 0-3.17-1.24-6.15-3.41-8.45ZM12.07 21.7h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.8 1 1.01-3.7-.23-.38a9.83 9.83 0 0 1-1.5-5.2c0-5.44 4.44-9.87 9.9-9.87 2.64 0 5.13 1.03 7 2.9a9.82 9.82 0 0 1 2.91 6.99c0 5.44-4.44 9.85-9.88 9.85Zm5.42-7.38c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.19-.24-.58-.48-.5-.66-.51-.17-.01-.37-.01-.57-.01-.2 0-.52.07-.79.37-.27.3-1.03 1.01-1.03 2.46 0 1.45 1.05 2.85 1.2 3.05.15.2 2.07 3.17 5.02 4.44.7.3 1.25.48 1.68.62.7.22 1.34.19 1.85.12.56-.08 1.76-.72 2.01-1.42.25-.7.25-1.29.18-1.42-.07-.13-.27-.2-.57-.35Z"/></svg>
);
const IconMessages = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="#0A84FF" aria-hidden><path d="M12 2.5C6.2 2.5 1.5 6.45 1.5 11.32c0 2.73 1.47 5.16 3.77 6.79-.2.8-.75 2.49-1.32 3.19-.2.25 0 .58.32.52 2.14-.42 3.86-1.29 4.72-1.79 1.02.24 2.1.37 3.01.37 5.8 0 10.5-3.95 10.5-8.82S17.8 2.5 12 2.5Z"/></svg>
);
const IconInstagram = (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
    <defs><linearGradient id="igGradShare" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#F58529"/><stop offset="45%" stopColor="#DD2A7B"/><stop offset="100%" stopColor="#8134AF"/>
    </linearGradient></defs>
    <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#igGradShare)"/>
    <circle cx="12" cy="12" r="4" fill="none" stroke="#fff" strokeWidth="1.6"/>
    <circle cx="17.5" cy="6.5" r="1.1" fill="#fff"/>
  </svg>
);

/**
 * ShareRow — invites the client to share their referral code.
 *
 *  • Desktop: inline pill row (unchanged UX — WhatsApp · iMessage · Instagram DM).
 *  • Mobile:  a single "SHARE" button that opens a CLAÈ-branded modal with
 *             every channel as a tile grid (WhatsApp, Messages, Instagram,
 *             Copy Message, and the native OS share sheet when available).
 *
 *  The outgoing message uses the same referral wording shown on the page and
 *  interpolates the CLIENT'S OWN referral code dynamically — never a hardcoded
 *  demo code.
 */
function ShareRow({ code, amount }) {
  const [modalOpen, setModalOpen] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const msg = `Send friends your code — they get £${amount} off their first CLAÈ appointment, and you get £${amount} credit towards your next.\n\nCode: ${code}\n${origin}`;
  const enc = encodeURIComponent(msg);
  const whatsAppHref = `https://wa.me/?text=${enc}`;
  const isIOS = typeof navigator !== "undefined" && /iP(ad|hone|od)/.test(navigator.userAgent);
  const smsHref = isIOS ? `sms:&body=${enc}` : `sms:?body=${enc}`;

  const openInstagram = async () => {
    try { await navigator.clipboard.writeText(msg); } catch {}
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title: "CLAÈ Aesthetics", text: msg }); return; } catch {}
    }
    const app = "instagram://direct/inbox";
    const web = "https://www.instagram.com/direct/inbox/";
    const t = setTimeout(() => window.open(web, "_blank"), 500);
    window.location.href = app;
    setTimeout(() => clearTimeout(t), 1200);
    toast.success("Message copied — paste it into any Instagram chat");
  };

  const iconBtn = "inline-flex items-center gap-2 px-3 py-2 border hairline bg-white hover:border-stone-900 transition text-xs mono-label text-stone-800";

  return (
    <div className="mt-4" data-testid="share-row">
      <span className="mono-label text-[10px] text-stone-500">SHARE VIA</span>

      {/* Desktop — inline pill row (existing UX preserved) */}
      <div className="hidden sm:flex flex-wrap items-center gap-2 mt-2" data-testid="share-row-desktop">
        <a href={whatsAppHref} target="_blank" rel="noreferrer" data-testid="share-whatsapp"
           aria-label="Share on WhatsApp" className={iconBtn}>{IconWhatsApp}WhatsApp</a>
        <a href={smsHref} data-testid="share-imessage" aria-label="Share via iMessage" className={iconBtn}>
          {IconMessages}iMessage
        </a>
        <button type="button" onClick={openInstagram} data-testid="share-instagram"
                aria-label="Share via Instagram DM" className={iconBtn}>{IconInstagram}Instagram DM</button>
      </div>

      {/* Mobile — single elegant SHARE button that opens the modal */}
      <div className="sm:hidden mt-2">
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          data-testid="share-mobile-open"
          aria-label="Open share options"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border hairline bg-white hover:border-stone-900 transition mono-label text-stone-900 tracking-[0.24em]"
        >
          <Share2 size={14} strokeWidth={1.5}/> SHARE
        </button>
      </div>

      {modalOpen && (
        <ShareModal
          code={code}
          msg={msg}
          whatsAppHref={whatsAppHref}
          smsHref={smsHref}
          openInstagram={openInstagram}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * ShareModal — CLAÈ-branded mobile share sheet.
 * Off-white surface, hairline border, rounded corners, subtle shadow.
 * Tap the overlay or the × button (or press Esc) to dismiss.
 */
function ShareModal({ code, msg, whatsAppHref, smsHref, openInstagram, onClose }) {
  const [canNative, setCanNative] = useState(false);
  useEffect(() => {
    setCanNative(typeof navigator !== "undefined" && !!navigator.share);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const nativeShare = async () => {
    try { await navigator.share({ title: "CLAÈ Aesthetics", text: msg }); onClose(); }
    catch { /* user dismissed */ }
  };
  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(msg); toast.success("Message copied"); }
    catch { toast.error("Copy failed"); }
  };

  const tileCls =
    "flex flex-col items-center justify-center gap-2 py-5 border hairline bg-white " +
    "hover:border-stone-900 active:scale-[0.98] transition text-stone-900";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-[2px] flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Share your CLAÈ referral code"
      data-testid="share-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-[#FAF9F6] border hairline rounded-lg shadow-xl p-6 pb-7"
        data-testid="share-modal"
        style={{ animation: "clae-share-in .2s ease-out" }}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="mono-label text-[10px] tracking-[0.24em] text-stone-500">SHARE THE GLOW</p>
            <p className="font-serif text-xl text-stone-900 mt-1 leading-tight">Send your CLAÈ code</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="share-modal-close"
            aria-label="Close"
            className="text-stone-500 hover:text-stone-900 p-1 -mr-1 -mt-1"
          >
            <X size={20} strokeWidth={1.4}/>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3" data-testid="share-modal-grid">
          <a href={whatsAppHref} target="_blank" rel="noreferrer" onClick={onClose}
             data-testid="share-modal-whatsapp" aria-label="Share on WhatsApp" className={tileCls}>
            {IconWhatsApp}
            <span className="mono-label text-[10px] text-stone-800">WhatsApp</span>
          </a>
          <a href={smsHref} onClick={onClose}
             data-testid="share-modal-messages" aria-label="Share via Messages" className={tileCls}>
            {IconMessages}
            <span className="mono-label text-[10px] text-stone-800">Messages</span>
          </a>
          <button type="button" onClick={() => { openInstagram(); onClose(); }}
                  data-testid="share-modal-instagram" aria-label="Share via Instagram" className={tileCls}>
            {IconInstagram}
            <span className="mono-label text-[10px] text-stone-800">Instagram</span>
          </button>
          <button type="button" onClick={copyMessage}
                  data-testid="share-modal-copy" aria-label="Copy the message and code" className={tileCls}>
            <Copy size={22} strokeWidth={1.3} className="text-stone-900"/>
            <span className="mono-label text-[10px] text-stone-800">Copy Message</span>
          </button>
          {canNative && (
            <button type="button" onClick={nativeShare}
                    data-testid="share-modal-native" aria-label="More sharing options"
                    className={`${tileCls} col-span-2`}>
              <Share2 size={22} strokeWidth={1.3} className="text-stone-900"/>
              <span className="mono-label text-[10px] text-stone-800">More options</span>
            </button>
          )}
        </div>

        <div className="mt-6 pt-5 border-t hairline">
          <p className="mono-label text-[10px] text-stone-500 mb-2">MESSAGE PREVIEW</p>
          <p className="text-[12px] leading-[1.7] text-stone-700 whitespace-pre-line" data-testid="share-modal-preview">
            {msg}
          </p>
        </div>
      </div>
      <style>{`@keyframes clae-share-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}

/** Convert a Europe/London wall-time (YYYY-MM-DD + HH:MM) into a real UTC Date, DST-aware. */
function londonToUtc(dateStr, timeStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  const naiveUtc = new Date(Date.UTC(y, m - 1, d, hh, mm));
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
  const g = Object.fromEntries(fmt.formatToParts(naiveUtc).map((p) => [p.type, p.value]));
  const londonMs = Date.UTC(+g.year, +g.month - 1, +g.day, +g.hour, +g.minute);
  return new Date(naiveUtc.getTime() - (londonMs - naiveUtc.getTime()));
}

function toUtcStamp(dt) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}00Z`;
}

function googleCalendarUrl(b, location, treatmentsLine) {
  const start = londonToUtc(b.date, b.time);
  const end = new Date(start.getTime() + (b.duration_min || 60) * 60 * 1000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `CLAÈ Aesthetics — ${treatmentsLine || "Appointment"}`,
    dates: `${toUtcStamp(start)}/${toUtcStamp(end)}`,
    details: `Booking reference: ${b.reference}\nTreatment: ${treatmentsLine}\nDuration: ${b.duration_min} min`,
    location: location || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function downloadIcs(b, location, treatmentsLine) {
  const pad = (n) => String(n).padStart(2, "0");
  const local = (d, t) => {
    const [y, m, dd] = d.split("-").map(Number);
    const [hh, mm] = t.split(":").map(Number);
    return `${y}${pad(m)}${pad(dd)}T${pad(hh)}${pad(mm)}00`;
  };
  const startLocal = local(b.date, b.time);
  const endMin = (Number(b.time.split(":")[0]) * 60 + Number(b.time.split(":")[1]) + (b.duration_min || 60));
  const endHH = Math.floor(endMin / 60) % 24;
  const endMM = endMin % 60;
  // Cross a day if the appointment runs past midnight London.
  const [yy, mm, dd] = b.date.split("-").map(Number);
  const endDate = new Date(Date.UTC(yy, mm - 1, dd, endHH, endMM));
  if (endMin >= 24 * 60) endDate.setUTCDate(endDate.getUTCDate() + 1);
  const endLocal = `${endDate.getUTCFullYear()}${pad(endDate.getUTCMonth() + 1)}${pad(endDate.getUTCDate())}T${pad(endHH)}${pad(endMM)}00`;
  const now = new Date();
  const dtstamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
  const esc = (s) => String(s || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CLAE Aesthetics//Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${b.reference}@clae-aesthetics`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;TZID=Europe/London:${startLocal}`,
    `DTEND;TZID=Europe/London:${endLocal}`,
    `SUMMARY:${esc(`CLAÈ Aesthetics — ${treatmentsLine || "Appointment"}`)}`,
    `DESCRIPTION:${esc(`Booking reference: ${b.reference}\nDuration: ${b.duration_min} min`)}`,
    `LOCATION:${esc(location || "")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `CLAE-${b.reference}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}
